export const PRICE_RULER_MARKER_COLORS = Object.freeze({
  green: {
    markerClass: 'bg-emerald-600 ring-emerald-100',
    textClass: 'text-emerald-700',
  },
  yellow: {
    markerClass: 'bg-amber-500 ring-amber-100',
    textClass: 'text-amber-700',
  },
  orange: {
    markerClass: 'bg-orange-500 ring-orange-100',
    textClass: 'text-orange-700',
  },
  gray: {
    markerClass: 'bg-slate-500 ring-slate-100',
    textClass: 'text-slate-700',
  },
});

export const PRICE_RULER_VARIANTS = Object.freeze({
  rent: {
    label: 'Aluguel',
    currencySuffix: '/mês',
    bandClass: 'bg-emerald-200',
  },
  sale: {
    label: 'Venda',
    currencySuffix: '',
    bandClass: 'bg-lais-primary-100',
  },
});

export const numberValue = value => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  const valueString = String(value).trim();
  if (!valueString) return 0;

  if (valueString.includes(',') && valueString.includes('.')) {
    return Number(valueString.replace(/\./g, '').replace(',', '.')) || 0;
  }

  if (valueString.includes(',')) {
    return Number(valueString.replace(',', '.')) || 0;
  }

  return Number(valueString) || 0;
};

export const clampPercentage = percentage =>
  Math.min(Math.max(Number(percentage) || 0, 0), 100);

export const priceRulerPosition = ({ value, min, max }) => {
  const minValue = numberValue(min);
  const maxValue = numberValue(max);
  const currentValue = numberValue(value);

  if (!minValue || !maxValue || maxValue <= minValue) return null;

  return clampPercentage(
    ((currentValue - minValue) / (maxValue - minValue)) * 100
  );
};

export const resolveRulerVariant = publicationType => {
  const normalized = String(publicationType || '').toLowerCase();
  return ['aluguel', 'temporada', 'rent'].includes(normalized)
    ? 'rent'
    : 'sale';
};

export const resolveMarkerColor = markerColor =>
  PRICE_RULER_MARKER_COLORS[markerColor] || PRICE_RULER_MARKER_COLORS.gray;

export const buildPriceRulerState = ({
  value,
  min,
  max,
  percentiles = {},
  markerColor = 'gray',
  variant = 'sale',
} = {}) => {
  const p25 = numberValue(percentiles.p25);
  const p50 = numberValue(percentiles.p50);
  const p75 = numberValue(percentiles.p75);
  const minValue = numberValue(min) || p25;
  const maxValue = numberValue(max) || p75;
  const markerValue = numberValue(value) || p50;
  const resolvedVariant = PRICE_RULER_VARIANTS[variant]
    ? variant
    : resolveRulerVariant(variant);

  const requiredValues = [minValue, maxValue, markerValue, p25, p50, p75];
  const hasData = requiredValues.every(item => item > 0) && maxValue > minValue;

  if (!hasData) {
    return {
      available: false,
      min: minValue,
      max: maxValue,
      value: markerValue,
      percentiles: { p25, p50, p75 },
      positions: {
        p25: 0,
        p50: 0,
        p75: 0,
        marker: 0,
        bandStart: 0,
        bandWidth: 0,
      },
      variant: resolvedVariant,
      variantConfig: PRICE_RULER_VARIANTS[resolvedVariant],
      markerColor: resolveMarkerColor(markerColor),
    };
  }

  const p25Position = priceRulerPosition({
    value: p25,
    min: minValue,
    max: maxValue,
  });
  const p50Position = priceRulerPosition({
    value: p50,
    min: minValue,
    max: maxValue,
  });
  const p75Position = priceRulerPosition({
    value: p75,
    min: minValue,
    max: maxValue,
  });
  const markerPosition = priceRulerPosition({
    value: markerValue,
    min: minValue,
    max: maxValue,
  });

  const bandStart = Math.min(p25Position, p75Position);
  const bandWidth = Math.abs(p75Position - p25Position);

  return {
    available: true,
    min: minValue,
    max: maxValue,
    value: markerValue,
    percentiles: { p25, p50, p75 },
    positions: {
      p25: p25Position,
      p50: p50Position,
      p75: p75Position,
      marker: markerPosition,
      bandStart,
      bandWidth,
    },
    variant: resolvedVariant,
    variantConfig: PRICE_RULER_VARIANTS[resolvedVariant],
    markerColor: resolveMarkerColor(markerColor),
  };
};
